import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconRoute } from '@tabler/icons-react';
import { BetaSchemaForm, ProFormColumnsType } from '@ant-design/pro-components';
import { CrudMode, FormItemValueType, SelectOptions } from '@ohpen/enso-ui';
import { Form } from 'antd';

import { TabProps } from '../interfaces';
import { FieldType, FirmGetById } from '@pmp/api';
import { CrudFormCard } from './CrudFormCard';
import { OPERATION_ORDER, operationToFieldTypeMap } from '../utils';

export const Payments: React.FC<TabProps> = ({ form, mode, selectsOptions }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;
  const watchedOperations = Form.useWatch(['registrationDetails', 'operations'], form);

  const operations: string[] = useMemo(() => {
    if (!watchedOperations) return [];
    return [...watchedOperations].sort((a, b) => OPERATION_ORDER.indexOf(a) - OPERATION_ORDER.indexOf(b));
  }, [watchedOperations]);

  const paymentRouteOptions: SelectOptions = useMemo(() => {
    if (!operations?.length) return [];

    return operations.flatMap(operation => {
      const fieldType = operationToFieldTypeMap[operation];
      if (!fieldType) return [];

      return selectsOptions[fieldType] ?? [];
    });
  }, [operations, selectsOptions]);

  const columns: ProFormColumnsType<FirmGetById, 'text'>[] = useMemo(
    () => [
      {
        valueType: FormItemValueType.group,
        gutter: [24, 0],
        columns: [
          {
            title: t('lender.firms.labels.defaultPaymentRoute'),
            dataIndex: ['payments', 'defaultPaymentRouteFirmId'],
            valueType: FormItemValueType.select,
            readonly,
            fieldProps: {
              optionFilterProp: 'label',
              showSearch: true,
              options: paymentRouteOptions,
            },
            formItemProps: { rules: [{ required: true }] },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.otherPaymentRoutes'),
            dataIndex: ['payments', 'paymentRouteFirmIds'],
            valueType: FormItemValueType.select,
            readonly,
            fieldProps: {
              mode: 'multiple',
              options: paymentRouteOptions,
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        title: t('lender.firms.labels.willReceiveDirectly'),
        dataIndex: ['payments', 'receivePaymentsDirectly'],
        valueType: FormItemValueType.radio,
        readonly,
        fieldProps: {
          options: [
            { label: t('global.labels.yes'), value: true },
            { label: t('global.labels.no'), value: false },
          ],
        },
        colProps: { span: 24 },
      },
      ...operations.map(operation => ({
        title: selectsOptions[FieldType.OPERATION_TYPE]?.find(opt => opt.value === operation)?.label,
        valueType: FormItemValueType.group,
        readonly,
        gutter: [24, 0],
        columns: [
          {
            title: t('lender.firms.labels.accountName'),
            dataIndex: ['payments', 'operations', operation, 'accountName'],
            valueType: FormItemValueType.text,
            readonly,
            formItemProps: { rules: [{ required: true }] },
            colProps: { span: 8 },
          },
          {
            title: t('lender.firms.labels.sortCode'),
            dataIndex: ['payments', 'operations', operation, 'sortCode'],
            valueType: FormItemValueType.text,
            readonly,
            colProps: { span: 8 },
          },
          {
            title: t('lender.firms.labels.accountNumber'),
            dataIndex: ['payments', 'operations', operation, 'accountNumber'],
            valueType: FormItemValueType.text,
            readonly,
            colProps: { span: 8 },
          },
        ],
      })),
    ],
    [selectsOptions, operations, readonly, t, paymentRouteOptions],
  );

  return (
    <CrudFormCard icon={<IconRoute />} title={t('lender.firms.labels.payments')}>
      <BetaSchemaForm<FirmGetById> form={form} layoutType="Embed" columns={columns} submitter={false} />
    </CrudFormCard>
  );
};
